import { DS, getImageSrc } from "./VitrineShared";
import { Heart, Plus, ShoppingBag } from "lucide-react";

// ─── Shirt Placeholder ───────────────────────────────────────────────────────
export function ShirtPlaceholder() {
  return (
    <div style={{ width:'100%', height:'100%', background:DS.sand, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
        <circle cx="36" cy="13" r="5" stroke={DS.gold} strokeWidth="1.5" fill="none"/>
        <line x1="36" y1="18" x2="36" y2="26" stroke={DS.gold} strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M36 26 C28 26 12 38 8 48" stroke={DS.gold} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        <path d="M36 26 C44 26 60 38 64 48" stroke={DS.gold} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        <line x1="6" y1="48" x2="66" y2="48" stroke={DS.gold} strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    </div>
  );
}

// ─── Skeleton Card ────────────────────────────────────────────────────────────
export function LuxeSkeletonCard() {
  return (
    <div style={{ background:DS.white, borderRadius:'12px', overflow:'hidden' }}>
      <div style={{ paddingTop:'133.33%', position:'relative', background:DS.sand }}>
        <div style={{
          position:'absolute', inset:0,
          background:`linear-gradient(90deg, ${DS.sand} 0%, ${DS.border} 50%, ${DS.sand} 100%)`,
          backgroundSize:'200% 100%', animation:'vShimmer 1.6s infinite linear'
        }} />
      </div>
      <div style={{ padding:'14px 16px 18px' }}>
        <div style={{ height:'9px', background:DS.sand, borderRadius:'4px', width:'38%', marginBottom:'9px' }} />
        <div style={{ height:'15px', background:DS.border, borderRadius:'4px', width:'72%', marginBottom:'10px' }} />
        <div style={{ height:'13px', background:DS.sand, borderRadius:'4px', width:'28%' }} />
      </div>
    </div>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────
export function LuxeProductCard({ product, onClick, isFavorite, onToggleFavorite, devise = '€', animDelay = 0 }) {
  const imageSrc = getImageSrc(product);
  const isOutOfStock = (product.total_quantity ?? 0) === 0;
  const category = product.category_name || product.category || '';
  const colorSwatches = (product.variants || []).reduce((acc, v) => {
    if (v.color_hex && !acc.includes(v.color_hex)) acc.push(v.color_hex);
    return acc;
  }, []);

  return (
    <div className="vr-card"
      data-testid={`product-card-${product.id}`}
      onClick={onClick}
      style={{
        background:DS.white, borderRadius:'12px', overflow:'hidden', cursor:'pointer',
        position:'relative', border:`1px solid ${DS.borderLight}`,
        transition:'transform 0.35s ease, box-shadow 0.35s ease', willChange:'transform',
        animation:`vFadeUp 0.5s ease ${animDelay}s both`,
        opacity: isOutOfStock ? 0.6 : 1,
      }}
    >
      {/* Image */}
      <div className="card-img" style={{ position:'relative', paddingTop:'100%', background:DS.sand, overflow:'hidden' }}>
        {imageSrc ? (
          <img src={imageSrc} alt={product.name}
            style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}
            loading="lazy" />
        ) : (
          <div style={{ position:'absolute', inset:0 }}><ShirtPlaceholder /></div>
        )}
        {/* Badges */}
        {isOutOfStock && (
          <div style={{ position:'absolute', top:10, left:10 }}>
            <span style={{ background:'rgba(26,23,20,0.85)', color:DS.cream, padding:'4px 10px',
              borderRadius:'20px', fontSize:'11px', fontWeight:500, letterSpacing:'0.02em' }}>
              Rupture
            </span>
          </div>
        )}
        {/* Favourite heart */}
        {onToggleFavorite && (
          <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(product.id); }}
            style={{ position:'absolute', top:10, right:10, width:32, height:32, borderRadius:'50%',
              background:'rgba(255,255,255,0.85)', backdropFilter:'blur(4px)',
              border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
              transition:'transform 0.2s' }}
            data-testid={`fav-btn-${product.id}`}>
            <Heart style={{ width:16, height:16, color: isFavorite ? '#E74C3C' : DS.stone,
              fill: isFavorite ? '#E74C3C' : 'none', transition:'all 0.3s' }} />
          </button>
        )}
        {/* Quick add button */}
        {!isOutOfStock && (
          <div style={{ position:'absolute', bottom:10, right:10, width:34, height:34, borderRadius:'50%',
            background:DS.ink, display:'flex', alignItems:'center', justifyContent:'center',
            cursor:'pointer', opacity:0.9, transition:'opacity 0.2s' }}>
            <Plus style={{ width:15, height:15, color:DS.cream }} />
          </div>
        )}
      </div>
      {/* Info */}
      <div style={{ padding:'12px 14px 16px' }}>
        {category && (
          <p style={{ fontFamily:'DM Sans,sans-serif', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.1em',
            color:DS.gold, fontWeight:600, marginBottom:5 }}>
            {category}
          </p>
        )}
        <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'17px', fontWeight:600,
          color:DS.ink, lineHeight:1.25, marginBottom:6 }}>
          {product.name}
        </h3>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:'14px', fontWeight:600, color:DS.ink }}>
            {product.sale_price?.toLocaleString('fr-FR')} {devise}
          </span>
          {colorSwatches.length > 0 && (
            <div style={{ display:'flex', gap:3 }}>
              {colorSwatches.slice(0,4).map((hex, i) => (
                <div key={i} style={{ width:12, height:12, borderRadius:'50%', backgroundColor:hex,
                  border:'1.5px solid rgba(255,255,255,0.9)', boxShadow:'0 1px 3px rgba(0,0,0,0.15)' }} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
